import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit,
  startAfter,
  getDocs,
  serverTimestamp,
  doc,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import {
  AcceptFriendRequest,
  FirstTimeListView,
  FollowedMe,
  FriendRequest,
  InviteAccepted,
  JoinedUser,
  ListProductLike,
  PostLike,
  ShoppingListChatMessage,
  ShoppingListInvite,
} from "../NotificationTypes";
import { UserAuth } from "../../Context";
import { db } from "../../FirebaseConfig";
import { GroupedNotification } from "../GroupNotification";
import PostComment from "../PostComment";
import ProductPriceChange from "../ProductPriceChange";
import LoadingSpinner from "../../UserProfile/LoadingSpinner";

const notificationComponents = {
  joined: JoinedUser,
  inviteAccepted: InviteAccepted,
  postLike: PostLike,
  postComment: PostComment,
  followed: FollowedMe,
  listChat: ShoppingListChatMessage,
  firstTimeListView: FirstTimeListView,
  listProductLike: ListProductLike,
  friendRequest: FriendRequest,
  acceptFriendRequest: AcceptFriendRequest,
  inviteOnList: ShoppingListInvite,
  productPriceChange: ProductPriceChange,
};

const PAGE_LIMIT = 12;

function AllNotifications() {
  const { user } = UserAuth();
  const [notifications, setNotifications] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true); // Track if there are more notifications
  const [newNotificationsCount, setNewNotificationsCount] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const fetchNotifications = async (lastVisible = null) => {
    setLoading(true);

    const notificationsRef = collection(db, `users/${user?.uid}/notifications`);
    // const now = Timestamp.now();
    // const twoWeeksAgo = now.toMillis() - 14 * 24 * 60 * 60 * 1000;
    let q = query(
      notificationsRef,
      // where("timestamp", ">", twoWeeksAgo),
      orderBy("timestamp", "desc"),
      limit(PAGE_LIMIT)
    );

    if (lastVisible) {
      q = query(
        notificationsRef,
        orderBy("timestamp", "desc"),
        startAfter(lastVisible),
        limit(PAGE_LIMIT)
      );
    }

    try {
      const querySnapshot = await getDocs(q);
      const notificationData = querySnapshot.docs.map((docSnapshot) => ({
        notificationId: docSnapshot.id,
        ...docSnapshot.data(),
      }));

      setNotifications((prevNotifications) => {
        const existingIds = new Set(
          prevNotifications.map((n) => n.notificationId)
        );
        const uniqueNotifications = notificationData.filter(
          (n) => !existingIds.has(n.notificationId)
        );
        return [...prevNotifications, ...uniqueNotifications];
      });

      // Update the last visible document for pagination
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(lastDoc);

      // Determine if there are more notifications to fetch
      setHasMore(notificationData.length === PAGE_LIMIT);
    } catch (error) {
      console.error("Error fetching notifications: ", error);
    } finally {
      setLoading(false);
    }
  };
  const updateLastSeenNotification = async () => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    try {
      await updateDoc(userRef, {
        lastSeenNotification: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating lastSeenNotification: ", error);
    }
  };

  useEffect(() => {
    if (!user) return;

    // User document listener
    const userDocRef = doc(db, "users", user.uid);
    const unsubscribeUserDoc = onSnapshot(userDocRef, (docSnapshot) => {
      const data = docSnapshot.data();

      // Update lastSeenNotification and lastSeenMsgs
      const updatedLastSeenNotification =
        data?.lastSeenNotification || new Date();

      // Notifications query
      const notificationsRef = collection(
        db,
        "users",
        user.uid,
        "notifications"
      );
      const notificationsQuery = query(
        notificationsRef,
        where("timestamp", ">", updatedLastSeenNotification)
      );

      const unsubscribeNotifications = onSnapshot(
        notificationsQuery,
        (snapshot) => {
          setNewNotificationsCount(snapshot.size);
        }
      );

      // Cleanup function for sub-collections
      return () => {
        unsubscribeNotifications();
      };
    });

    // Cleanup for user document listener
    return () => {
      unsubscribeUserDoc();
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications(); // Fetch initial notifications
      updateLastSeenNotification(); // Update the lastSeenNotification timestamp
    }
  }, [user]);

  const handleLoadMore = () => {
    if (lastVisible && hasMore) {
      fetchNotifications(lastVisible);
    }
  };
  const renderNotification = (notification) => {
    // Check if the id is an array and if its length is greater than 1
    if (Array.isArray(notification.id) && notification.id.length > 1) {
      // Grouped notifications
      return (
        <GroupedNotification
          key={notification.notificationId}
          notification={notification}
          groupedNotifications={notification}
          user={user}
        />
      );
    }

    // Single notification
    const NotificationComponent = notificationComponents[notification.type];
    if (!NotificationComponent) return null;
    return (
      <NotificationComponent
        key={notification.notificationId}
        notification={notification}
        user={user}
      />
    );
  };
  if (loading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      <div
        className="m-0 px-0 vstack"
        style={{ paddingTop: isSmallScreen ? "0" : "10px" }}
      >
        <div
          className="card-header rounded-2 rounded-bottom-0 d-flex align-items-center justify-content-between gap-2 border-bottom bg-white"
          style={{
            padding: "10px 0",
            position: "sticky",
            top: "4.4rem",
            zIndex: 1,
          }}
        >
          <div className="d-flex align-items-center justify-content-between w-100">
            <div
              className="mb-0"
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "black",
              }}
            >
              Notifications
            </div>
            <span
              className="badge rounded-circle d-flex justify-content-center align-items-center"
              style={{
                width: "25px",
                height: "25px",
                fontSize: "12px",
                backgroundColor: "#0085FF",
              }}
            >
              {newNotificationsCount ?? 0}
            </span>
          </div>
          <div></div>
          {/* <!-- Notification action START --> */}
          {/* <div className="dropdown">
              <a
                href="#"
                className="text-secondary btn btn-secondary-soft-hover py-1 px-2"
                id="cardNotiAction"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-three-dots"></i>
              </a>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="cardNotiAction"
              >
                <li>
                  <a className="dropdown-item" href="#">
                    {" "}
                    <i className="bi bi-check-lg fa-fw pe-2"></i>Mark all read
                  </a>
                </li>
                <li>
                  <a className="dropdown-item" href="#">
                    {" "}
                    <i className="bi bi-bell-slash fa-fw pe-2"></i>Push
                    notifications{" "}
                  </a>
                </li>
                <li>
                  <a className="dropdown-item" href="#">
                    {" "}
                    <i className="bi bi-bell fa-fw pe-2"></i>Email notifications{" "}
                  </a>
                </li>
              </ul>
            </div> */}
          {/* <!-- Notification action END --> */}
        </div>
        <div className="card border-0">
          <div
            className={`card-body border rounded-top-0 border-top-0 ${
              notifications.length > 0 ? "border-bottom-0" : "rounded-2"
            } p-0`}
            style={{ padding: "0.625rem 0rem" }}
          >
            {notifications.length > 0 ? (
              <ul className="list-unstyled p-0 pt-0 m-0">
                {notifications.map(renderNotification)}
              </ul>
            ) : (
              !loading && (
                <>
                  <div
                    style={{
                      whiteSpace: "normal",
                      width: "100%",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      color: `rgb(0, 0, 0, 0.5)`,
                      fontSize: "20px",
                      lineHeight: "1.5rem",
                      letterSpacing: "0px",
                      minHeight: "65vh",
                    }}
                    className="text-center m-0 p-4 h-100 d-flex align-items-center justify-content-center"
                  >
                    No notifications yet...
                  </div>
                </>
              )
            )}
          </div>
          {hasMore && (
            <div className="card-footer border-0 py-3 text-center position-relative d-grid mt-2 pt-0 px-0">
              {/* <!-- Load more button START --> */}
              <a
                role="button"
                className={`btn btn-loader btn-primary-soft ${
                  loading ? "disabled" : ""
                }`}
                onClick={() => handleLoadMore()}
              >
                <span className="load-text">
                  {loading ? "Loading..." : "Load more notifications"}
                </span>
                <div className="load-icon">
                  <div className="spinner-grow spinner-grow-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </a>
              {/* <!-- Load more button END --> */}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AllNotifications;
